import React, { useState, useEffect } from 'react';
import { X, Sparkles, Zap, PartyPopper, ArrowRight, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * WelcomeTour - Interactive 3-step tour that guides users through the platform magic
 * 
 * Step 1: Welcome - First login, encourages adding first lead
 * Step 2: Magic in Progress - Shows after lead is added, automation status
 * Step 3: Complete - Shows after automation runs, celebrates success
 */
const WelcomeTour = ({ onComplete, onNavigate }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [automationStatus, setAutomationStatus] = useState({
    email: false,
    whatsapp: false,
    call: false
  });

  useEffect(() => {
    // Check if tour should be shown
    const tourCompleted = localStorage.getItem('welcome_tour_completed');
    const tourStep = localStorage.getItem('welcome_tour_step');
    
    if (tourCompleted === 'true') {
      setIsVisible(false);
      return;
    }
    
    // Resume from last step if available
    if (tourStep) {
      setCurrentStep(parseInt(tourStep, 10));
    }
    
    // Show tour for new users
    const isNewUser = !localStorage.getItem('has_added_lead');
    if (isNewUser || tourStep) {
      setIsVisible(true);
    }
  }, []);

  useEffect(() => {
    // Save current step
    localStorage.setItem('welcome_tour_step', currentStep.toString());
  }, [currentStep]);

  // Poll for automation status when on step 2
  useEffect(() => {
    if (currentStep !== 2) return;

    const checkAutomation = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const leadId = localStorage.getItem('latest_lead_id');
        
        if (!leadId) return;

        const res = await fetch(`${API_URL}/api/leads/${leadId}/timeline`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (res.ok) {
          const data = await res.json();
          const timeline = data.timeline || [];
          
          const hasEmail = timeline.some(e => 
            e.type.includes('email') || e.message.toLowerCase().includes('email')
          );
          const hasWhatsapp = timeline.some(e => 
            e.type.includes('whatsapp') || e.message.toLowerCase().includes('whatsapp')
          );
          const hasCall = timeline.some(e => 
            e.type.includes('call') || e.message.toLowerCase().includes('call')
          );
          
          setAutomationStatus({ email: hasEmail, whatsapp: hasWhatsapp, call: hasCall });
          
          // If all automation is done, advance to step 3
          if (hasEmail && hasWhatsapp && hasCall) {
            setTimeout(() => setCurrentStep(3), 1500);
          }
        }
      } catch (err) {
        console.error('Error checking automation:', err);
      }
    };

    checkAutomation();
    const interval = setInterval(checkAutomation, 10000); // Check every 10 seconds
    
    return () => clearInterval(interval);
  }, [currentStep]);

  const handleClose = () => {
    setIsVisible(false);
    localStorage.setItem('welcome_tour_completed', 'true');
    if (onComplete) onComplete();
  };

  const handleAddLead = () => {
    // Navigate to leads page and trigger add lead
    if (onNavigate) {
      onNavigate('/leads?action=add');
    }
    setCurrentStep(2);
    localStorage.setItem('has_added_lead', 'true');
  };

  const handleViewTimeline = () => {
    const leadId = localStorage.getItem('latest_lead_id');
    if (onNavigate && leadId) {
      onNavigate(`/leads?view=${leadId}`);
    }
  };

  const handleViewPipeline = () => {
    if (onNavigate) {
      onNavigate('/leads');
    }
    handleClose();
  };

  if (!isVisible) return null;

  const steps = [
    // Step 1: Welcome
    {
      icon: <Sparkles className="w-12 h-12 text-yellow-400" />,
      title: "Welcome to AILeadMantra!",
      subtitle: "Let us show you the magic!",
      description: "Add your first lead and watch automation work in real time! No setup required - everything happens automatically.",
      cta: "Add Your First Lead",
      ctaIcon: <ArrowRight className="w-4 h-4 ml-2" />,
      onAction: handleAddLead
    },
    // Step 2: Magic in Progress (not used as we jump from 0 to 2)
    null,
    // Step 2: Magic in Progress
    {
      icon: <Zap className="w-12 h-12 text-blue-400 animate-pulse" />,
      title: "Magic in Progress!",
      subtitle: "Your automation is running...",
      description: null,
      customContent: (
        <div className="space-y-3 my-4">
          <div className={`flex items-center gap-3 p-3 rounded-lg ${automationStatus.email ? 'bg-green-900/30 text-green-400' : 'bg-gray-800/50 text-gray-400'}`}>
            {automationStatus.email ? <CheckCircle2 className="w-5 h-5" /> : <div className="w-5 h-5 rounded-full border-2 border-dashed animate-spin" />}
            <span>Welcome Email {automationStatus.email ? 'Sent!' : 'Sending...'}</span>
          </div>
          <div className={`flex items-center gap-3 p-3 rounded-lg ${automationStatus.whatsapp ? 'bg-green-900/30 text-green-400' : 'bg-gray-800/50 text-gray-400'}`}>
            {automationStatus.whatsapp ? <CheckCircle2 className="w-5 h-5" /> : <div className="w-5 h-5 rounded-full border-2 border-dashed" style={{ animationDelay: '5s' }} />}
            <span>WhatsApp Message {automationStatus.whatsapp ? 'Sent!' : '(in 10 mins)'}</span>
          </div>
          <div className={`flex items-center gap-3 p-3 rounded-lg ${automationStatus.call ? 'bg-green-900/30 text-green-400' : 'bg-gray-800/50 text-gray-400'}`}>
            {automationStatus.call ? <CheckCircle2 className="w-5 h-5" /> : <div className="w-5 h-5 rounded-full border-2 border-dashed" />}
            <span>AI Call {automationStatus.call ? 'Completed!' : '(in 15 mins)'}</span>
          </div>
        </div>
      ),
      cta: "View Lead Timeline",
      ctaIcon: <ArrowRight className="w-4 h-4 ml-2" />,
      onAction: handleViewTimeline
    },
    // Step 3: Complete
    {
      icon: <PartyPopper className="w-12 h-12 text-purple-400" />,
      title: "Full Automation Complete!",
      subtitle: "Your lead is now being followed up!",
      description: "Email, WhatsApp, and AI Call - all done automatically! Your lead is now on a 14-day follow-up sequence. You never miss a single lead!",
      cta: "View Your Pipeline",
      ctaIcon: <ArrowRight className="w-4 h-4 ml-2" />,
      onAction: handleViewPipeline
    }
  ];

  const step = steps[currentStep] || steps[0];

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-2xl p-8 max-w-md mx-4 border border-emerald-500/30 shadow-2xl shadow-emerald-500/20 animate-in fade-in zoom-in duration-300">
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Content */}
        <div className="text-center">
          {/* Icon */}
          <div className="flex justify-center mb-4">
            {step.icon}
          </div>
          
          {/* Title */}
          <h2 className="text-2xl font-bold text-white mb-2">
            {step.title}
          </h2>
          
          {/* Subtitle */}
          <p className="text-emerald-400 font-medium mb-4">
            {step.subtitle}
          </p>
          
          {/* Description or Custom Content */}
          {step.customContent ? (
            step.customContent
          ) : (
            <p className="text-gray-300 mb-6">
              {step.description}
            </p>
          )}
          
          {/* CTA Button */}
          <Button
            onClick={step.onAction}
            className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-semibold py-6 text-lg rounded-xl"
          >
            {step.cta}
            {step.ctaIcon}
          </Button>
          
          {/* Skip link */}
          <button
            onClick={handleClose}
            className="mt-4 text-sm text-gray-500 hover:text-gray-300 transition-colors"
          >
            Skip tour
          </button>
        </div>

        {/* Step indicator */}
        <div className="flex justify-center gap-2 mt-6">
          {[0, 2, 3].map((stepNum, idx) => (
            <div
              key={stepNum}
              className={`w-2 h-2 rounded-full transition-colors ${
                currentStep >= stepNum ? 'bg-emerald-500' : 'bg-gray-600'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default WelcomeTour;
